import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem, ReaddirResult } from '@capacitor/filesystem';
import write_blob from 'capacitor-blob-writer';
import produce from 'immer';
import { TBucketId, TBucketInfo, TBucketName, TBucketUploadFile } from './bucket-storage.types';

@Injectable({
  providedIn: 'root',
})
export class BucketStorageService {
  constructor() {}

  /**
   *
   * @param uri Absolute path of the file
   * @returns
   */
  async getFileInfoByUri(uri: TBucketInfo['files'][number]['uri']): Promise<TBucketInfo['files'][number]> {
    return await Filesystem.stat({
      path: uri,
    }).then((v) => ({
      ...v,
      name: uri.split('/').pop() || 'undefined_file_name',
      publicPath: Capacitor.convertFileSrc(v.uri),
    }));
  }

  /**
   * Save file in bucket by moving it from cache
   * @param bucketInfo bucket where to store the file
   * @param fileInfo File to be sotred in the bucket
   * @returns
   */
  async saveFile(bucketInfo: TBucketInfo, fileInfo: TBucketInfo['files'][number]) {
    const [bucketPath, relativeFilePath] = this.#getPathTupleInBucket(bucketInfo, fileInfo.name);
    await this.#createBucketDir(bucketPath);
    await this.#moveFileToBucket(fileInfo, relativeFilePath);
  }

  convertBlobToBase64(blob: Blob): Promise<{ status: false } | { status: true; data: string }> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => {
        reject({
          status: false,
        });
      };
      reader.onload = () => {
        if (reader.result) {
          resolve({
            status: true,
            data: reader.result.toString(),
          });
        } else
          reject({
            status: false,
          });
      };
      reader.readAsDataURL(blob);
    });
  }

  async getBlobFromUrl(url: string): Promise<{ status: false } | { status: true; blob: Blob }> {
    try {
      const response = await fetch(url);
      console.log(response, 'getBase64FileFromServerUrl', url);
      const blob = await response.blob();
      console.log(blob, 'blob');
      return { status: true, blob };
    } catch (error) {
      return { status: false };
    }
  }

  async addImagesBucketForServerDefectives(
    bucketId: TBucketId,
    urls: string[],
  ): Promise<{ status: false } | { status: true; publicPaths: string[] }> {
    try {
      const path = this.#getBucketPathByIdAndBucketName(bucketId, 'server_defectives');
      await this.#createFreshBucketDir(path);
      const fileUri: string[] = [];
      let count = 0;
      for (let url of urls) {
        const base64Response = await this.getBlobFromUrl(url);
        if (base64Response.status) {
          await write_blob({
            directory: Directory.Data,
            path: path + `/image_${count}.jpg`,
            blob: base64Response.blob,
            recursive: true,
          });
        }
        count++;
      }
      const response = await Filesystem.readdir({
        directory: Directory.Data,
        path: path,
      });
      response.files.forEach((file) => {
        fileUri.push(Capacitor.convertFileSrc(file.uri));
      });
      return fileUri.length === urls.length
        ? {
            status: true,
            publicPaths: fileUri,
          }
        : {
            status: false,
          };
    } catch (error) {
      console.log('something went wrong adding bucket with urls', bucketId, urls, error);
      return {
        status: false,
      };
    }
  }

  async addImagesBucketListForServerDefectives(bucketListInfo: { bucketId: TBucketId; urls: string[] }[]) {
    const bucketIdToUrls: Record<TBucketId, string[]> = {};
    for (let bucketInfo of bucketListInfo) {
      const res = await this.addImagesBucketForServerDefectives(bucketInfo.bucketId, bucketInfo.urls);
      console.log('addImagesBucketListForServerDefectives', bucketListInfo, res);
      if (res.status) {
        bucketIdToUrls[bucketInfo.bucketId] = res.publicPaths;
      }
    }
    return bucketIdToUrls;
  }

  /**
   * Get All files in a bucket
   * @param bucketInfo bucket details
   * @returns Promise<TLiftedFileInfo[]>
   */
  async getAllFiles(bucketInfo: TBucketInfo) {
    const bucketPath = this.#getBucketPath(bucketInfo);
    try {
      const fileInfoList = await Filesystem.readdir({
        directory: Directory.Data,
        path: bucketPath,
      });
      const enhancedFileInfoList: TBucketInfo['files'] = fileInfoList.files.map((file) => ({
        ...file,
        publicPath: Capacitor.convertFileSrc(file.uri),
      }));
      return enhancedFileInfoList;
    } catch (err) {
      console.log(err, 'Error from getAllFiles');
      console.log('Directory not found');
      return [];
    }
  }

  #readdir(directory: Directory, path: string): Promise<{ status: false } | { status: true; response: ReaddirResult }> {
    return Filesystem.readdir({
      directory: directory,
      path: path,
    })
      .then((res) => {
        return {
          status: true,
          response: res,
        };
      })
      .catch((res) => {
        return {
          status: false,
        };
      });
  }

  async removeAllDefectiveFilesExceptBucketIds(bucketIds: TBucketId[]) {
    const bucketPath = this.#getBucketNameDirectoryPath('defectives');
    try {
      const fileInfoList = await this.#readdir(Directory.Data, bucketPath);
      if (fileInfoList.status) {
        for (let file of fileInfoList.response.files) {
          if (bucketIds.every((bucketId) => bucketId !== file.name)) {
            await Filesystem.rmdir({
              path: file.uri,
              recursive: true,
            });
          }
        }
      }
      return true;
    } catch (err) {
      console.log('Something went wrong removing all defective files except bucketIds', bucketIds);
      return false;
    }
  }

  removeServerDefectivesDirectory() {
    const bucketPath = this.#getBucketNameDirectoryPath('server_defectives');
    return Filesystem.rmdir({
      path: bucketPath,
      directory: Directory.Data,
      recursive: true,
    })
      .then(() => {
        return true;
      })
      .catch((error) => {
        console.log(`Failed to removie ${bucketPath} directory`);
        return false;
      });
  }

  async getAllDefectiveFilesByBucketId(bucketId: TBucketId) {
    const bucketPath = this.#getBucketPathByIdAndBucketName(bucketId, 'defectives');
    try {
      const fileInfoList = await Filesystem.readdir({
        directory: Directory.Data,
        path: bucketPath,
      });
      const enhancedFileInfoList: TBucketInfo['files'] = fileInfoList.files.map((file) => ({
        ...file,
        publicPath: Capacitor.convertFileSrc(file.uri),
      }));
      return enhancedFileInfoList;
    } catch (err) {
      console.log(err, 'Error from getAllFiles');
      console.log('Directory not found');
      return [];
    }
  }

  isBucketValid(bucketInfo: TBucketInfo) {
    return bucketInfo.files.length > 0;
  }

  /**
   * Rename Temprorary image bucket with commit id (here commit id can be eventId, or any unique id)
   * @param commitId
   * @param bucketInfo
   * @returns
   */
  async commitBucket(
    commitId: TBucketInfo['id'],
    bucketInfo: TBucketInfo,
  ): Promise<{ status: false } | { status: true; updatedBucket: TBucketInfo }> {
    console.log('Commiting Bucket', commitId, bucketInfo);
    const bucketPath = this.#getBucketPath(bucketInfo);
    let updatedBucket: TBucketInfo = {
      ...bucketInfo,
      id: commitId,
    };
    const updtedBucketPath = this.#getBucketPath(updatedBucket);

    try {
      console.log('BucketPath', bucketPath, 'updated bucket path', updtedBucketPath);
      await Filesystem.rename({
        directory: Directory.Data,
        from: bucketPath,
        to: updtedBucketPath,
      });
      console.log('Renaming successful');
      const enhancedFileInfoList = await this.getAllFiles(updatedBucket);
      return {
        status: true,
        updatedBucket: produce(updatedBucket, (draft) => {
          draft.files = enhancedFileInfoList;
        }),
      };
    } catch (err) {
      console.log('Commit Bucket Error', err);
      return {
        status: false,
      };
    }
  }
  /**
   * Removes bucket from App DATA storage
   * @param bucketInfo Bucet information to remove
   * @returns
   */
  async remove(bucketInfo: TBucketInfo) {
    const bucketPath = this.#getBucketPath(bucketInfo);
    return Filesystem.rmdir({
      directory: Directory.Data,
      path: bucketPath,
      recursive: true,
    })
      .then(() => {
        return true;
      })
      .catch((err) => {
        console.log('Remving', err);
        return false;
      });
  }

  /**
   * Remove file by Absolute file path
   * @param uri Absolute path in file system Android
   * @returns Promise<boolean>
   */
  async removeFileByUri(uri: TBucketInfo['files'][number]['uri']) {
    try {
      await Filesystem.deleteFile({
        path: uri,
      });
      return true;
    } catch (err) {
      console.log('[CapacitorFileSerive] Problem Removing file', err);
      return false;
    }
  }

  async loadFileData(file: TBucketInfo['files'][number]): Promise<TBucketUploadFile> {
    const readFile = await Filesystem.readFile({
      path: file.uri,
    });
    return {
      fileInfo: file,
      data: `data:image/jpeg;base64,${readFile.data}`,
    };
  }

  /**
   * Create Bucket Directory to store files
   * @param bucketPath create from TBucketInfo
   */
  async #createBucketDir(bucketPath: string) {
    try {
      await Filesystem.mkdir({
        directory: Directory.Data,
        path: bucketPath,
        recursive: true,
      });
    } catch (err) {
      console.log('[CapacitorFilesSerive] Directory already present');
    }
  }

  async #createFreshBucketDir(bucketPath: string) {
    try {
      await Filesystem.rmdir({
        directory: Directory.Data,
        path: bucketPath,
        recursive: true,
      });
      this.#createBucketDir(bucketPath);
    } catch (err) {
      this.#createBucketDir(bucketPath);
      console.log('[CapacitorFilesSerive] Directory already present');
    }
  }

  /**
   * Move file from cache to Bucket
   * @param fileInfo file details to be moved
   * @param to relative path of file from DATA directory
   */
  async #moveFileToBucket(fileInfo: TBucketInfo['files'][number], to: string) {
    try {
      await Filesystem.rename({
        from: fileInfo.uri,
        to,
        toDirectory: Directory.Data,
      });
    } catch (err) {
      console.log('[CapacitorFilesSerive] Directory already present');
    }
  }

  #getBucketNameDirectoryPath(name: TBucketName) {
    return ['SOLVEI8_BUCKET', name].join('/');
  }

  #getBucketPath(bucketInfo: TBucketInfo) {
    return this.#getBucketPathByIdAndBucketName(bucketInfo.id, bucketInfo.name);
  }

  #getBucketPathByIdAndBucketName(bucketId: TBucketId, name: TBucketName) {
    return ['SOLVEI8_BUCKET', name, bucketId].join('/');
  }

  #getPathTupleInBucket(bucketInfo: TBucketInfo, fileName: string): TBucketPathTuple {
    const bucketPath = this.#getBucketPathByIdAndBucketName(bucketInfo.id, bucketInfo.name);
    const filePath = [bucketPath, fileName].join('/');
    return [bucketPath, filePath];
  }
}

type TBucketPathTuple = [bucketPath: string, filePath: string];
